import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import InvitationBack from './InvitationBack.svg';
import MapImage from './Map.jpeg';
import SwayLogo from './SwayWhiteLogo.png';
import CheckinAreaBack from './Rectangle184-7.png';
import { API_BASE_URL } from '../Api';

interface VoteAreaPageProps {
    // 必要に応じてpropsを追加
  }
  
  // ルートパラメータの型定義
  interface VoteParams {
    areaId: string;
    [key: string]: string | undefined;
  }
  
  // エリア情報の型定義
  interface AreaInfo {
    area_id: string;
    area_name: string;
    area_status: string;
    creator_uid: string | null;
    creator_nickname: string;
    current_votes: number;
    creator_invite_code: string | null;
    voters_info: Array<{
      vote_uid: string;
      nickname: string;
      medium_icon_url: string;
      voted_at: string;
    }>;
    is_votable: boolean;
    votable_message: string;
    vote_url: string;
    latitude?: number;
    longitude?: number;
    geohash?: string[];
    created_at: string;
  }

  // 🆕 地名情報の型定義
  interface LocationInfo {
    city: string;        // 市区町村 (例: 渋谷区, 新宿区, 柏市)
    prefecture: string;  // 都道府県 (例: 東京都, 千葉県)
    country: string;     // 国 (例: 日本)
  }
  
  const VoteAreaPage: React.FC<VoteAreaPageProps> = () => {
    const { areaId } = useParams<VoteParams>();
    const [areaInfo, setAreaInfo] = useState<AreaInfo | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [isVoting, setIsVoting] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    const [manualCopyNeeded, setManualCopyNeeded] = useState<boolean>(false);
    
    // 🆕 地名情報のstate
    const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
    const [locationLoading, setLocationLoading] = useState<boolean>(false);

    // 🆕 座標から地名を取得する関数
    const getLocationFromCoordinates = async (latitude: number, longitude: number): Promise<LocationInfo | null> => {
      try {
        setLocationLoading(true);
        
        // 🌟 Google Geocoding API使用 (要API Key)
        // const response = await fetch(
        //   `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&language=ja&key=YOUR_API_KEY`
        // );
        
        // 🌟 無料のOpenStreetMap Nominatim API使用（推奨）
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=ja&addressdetails=1`
        );

        if (!response.ok) {
          throw new Error('地名取得APIエラー');
        }

        const data = await response.json();

        // 🌟 OpenStreetMapの場合のデータ解析
        if (data.address) {
          const address = data.address;
          
          // 市区町村の取得（優先順位で取得）
          const city = address.city || 
                      address.town || 
                      address.village || 
                      address.municipality || 
                      address.suburb ||
                      address.city_district ||
                      address.district ||
                      '不明';

          const prefecture = address.state || 
                           address.province || 
                           address.prefecture ||
                           '不明';

          const country = address.country || '日本';

          return {
            city,
            prefecture,
            country
          };
        }

        return null;
      } catch (error) {
        console.error('🚨 地名取得エラー:', error);
        return null;
      } finally {
        setLocationLoading(false);
      }
    };

    // 🆕 エリア名から地名を抽出する関数（フォールバック用）
    const extractLocationFromAreaName = (areaName: string): string => {
      // よくある地名パターンをマッピング
      const locationMappings: { [key: string]: string } = {
        '渋谷スクランブル交差点': '渋谷区',
        '渋谷駅': '渋谷区',
        '新宿駅': '新宿区',
        '新宿': '新宿区',
        '柏駅': '柏市',
        '柏': '柏市',
        '池袋駅': '豊島区',
        '池袋': '豊島区',
        '東京駅': '千代田区',
        '原宿': '渋谷区',
        '表参道': '港区',
        '銀座': '中央区',
        '浅草': '台東区',
        '上野': '台東区',
        '秋葉原': '千代田区',
        '品川': '港区',
        '六本木': '港区',
        '赤坂': '港区',
        '青山': '港区',
      };

      // 完全一致チェック
      if (locationMappings[areaName]) {
        return locationMappings[areaName];
      }

      // 部分一致チェック
      for (const [key, value] of Object.entries(locationMappings)) {
        if (areaName.includes(key.replace('駅', '').replace('スクランブル交差点', ''))) {
          return value;
        }
      }

      // デフォルト
      return areaName;
    };

    // エリア情報を取得
    useEffect(() => {
      const fetchAreaInfo = async () => {
        if (!areaId) {
          setError('エリアIDが指定されていません');
          setIsLoading(false);
          return;
        }
  
        try {
          setIsLoading(true);
          setError('');
  
          const response = await fetch(
            `${API_BASE_URL}/vote/area-info/?area_id=${areaId}`
          );
  
          if (!response.ok) {
            throw new Error('エリア情報の取得に失敗しました');
          }
  
          const data = await response.json();
  
          if (data.status === 'success') {
            setAreaInfo(data);
            
            // 🆕 座標がある場合は地名を取得
            if (data.latitude && data.longitude) {         
              const location = await getLocationFromCoordinates(data.latitude, data.longitude);
              if (location) {
                setLocationInfo(location);
              } else {
                // フォールバック: エリア名から推測
                const fallbackLocation = extractLocationFromAreaName(data.area_name);
                setLocationInfo({
                  city: fallbackLocation,
                  prefecture: '東京都', // デフォルト
                  country: '日本'
                });
              }
            } else {
              // 座標がない場合はエリア名から推測
              const fallbackLocation = extractLocationFromAreaName(data.area_name);
              setLocationInfo({
                city: fallbackLocation,
                prefecture: '東京都',
                country: '日本'
              });
              console.log('📍 座標なし、エリア名から推測:', fallbackLocation);
            }
          } else {
            setError(data.message || 'エラーが発生しました');
          }
        } catch (err) {
          console.error('エリア情報取得エラー:', err);
          setError('エリア情報の取得に失敗しました');
        } finally {
          setIsLoading(false);
        }
      };
  
      fetchAreaInfo();
    }, [areaId]);

    // 🆕 表示用の地名を取得する関数
    const getDisplayLocationName = (): string => {
      if (locationInfo) {
        return locationInfo.city;
      }
      // フォールバック
      return areaInfo ? extractLocationFromAreaName(areaInfo.area_name) : '';
    };

    // クリップボードにコピー
    const copyToClipboard = (text: string) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text)
          .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 3000);
          })
          .catch(err => {
              console.error('クリップボードコピーエラー:', err);
              fallbackCopyToClipboard(text);
          });
      } else {
          fallbackCopyToClipboard(text);
      }
    };

    // フォールバックのコピーメソッド
    const fallbackCopyToClipboard = (text: string) => {
      try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.top = '0';
          textArea.style.left = '0';
          textArea.style.position = 'fixed';
          
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          const successful = document.execCommand('copy');
          
          if (successful) {
              setCopied(true);
              setTimeout(() => setCopied(false), 3000);
          } else {
            setManualCopyNeeded(true);
          }
          
          document.body.removeChild(textArea);
      } catch (error) {
          console.error('フォールバックコピーエラー:', error);
          setManualCopyNeeded(true);
      }
    };

    // App Store に移動
    const goToAppStore = () => {
        const appStoreUrl = 'https://apps.apple.com/jp/app/sway-%E3%82%B9%E3%82%A6%E3%82%A7%E3%82%A4/id6738626019';
        window.location.href = appStoreUrl;
    };

    const getCodeFromUrl = (): string | null => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('code');
    };

    const generateDeepLinkUrl = (areaId: string): string => {
      // URLからinviteCodeを取得
      const inviteCode = getCodeFromUrl();
      
      if (inviteCode) {
        console.log(`🔗 招待コード付きディープリンク生成: sway://spots/${areaId}/code=${inviteCode}`);
        return `sway://spots/${areaId}/code=${inviteCode}`;
      } else {
        console.log(`⚠️ 招待コードなしディープリンク生成: sway://spots/${areaId}`);
        return `sway://spots/${areaId}`;
      }
    };
  
    // ローディング画面
    if (isLoading) {
      return (
        <div className="invitation-page-container" style={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          margin: 0,
          padding: 0,
          minHeight: '100vh',
          backgroundImage: `url(${CheckinAreaBack})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'sans-serif',
          position: 'relative'
        }}>
          {/* ガラスモーフィズムのローディングカード */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '40px 60px',
            textAlign: 'center',
            boxShadow: '0 25px 45px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid rgba(255, 255, 255, 0.8)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 24px'
            }} />
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontSize: '18px', 
              fontWeight: '600',
              margin: 0
            }}>
              読み込み中...
            </p>
          </div>
        </div>
      );
    }
  
    // エラー画面
    if (error || !areaInfo) {
      return (
        <div className="invitation-page-container" style={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          margin: 0,
          padding: 0,
          minHeight: '100vh',
          backgroundImage: `url(${CheckinAreaBack})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'sans-serif',
          position: 'relative'
        }}>
          {/* ガラスモーフィズムのエラーカード */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '40px',
            textAlign: 'center',
            maxWidth: '400px',
            margin: '20px',
            boxShadow: '0 25px 45px rgba(0, 0, 0, 0.1)',
          }}>
            <h2 style={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              marginBottom: '20px',
              fontSize: '24px',
              fontWeight: '700'
            }}>
              エラー
            </h2>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              marginBottom: '30px',
              fontSize: '16px',
              lineHeight: '1.5'
            }}>
              {error || 'エリア情報が見つかりません'}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '14px 28px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '16px',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              再読み込み
            </button>
          </div>
        </div>
      );
    }
  
    const targetVotes = 10;
    const remainingVotes = Math.max(0, targetVotes - areaInfo.current_votes);
  
    return (
      <div className="invitation-page-container" style={{
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        margin: 0,
        padding: 0,
        minHeight: '110vh',
        backgroundImage: `url(${CheckinAreaBack})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'sans-serif',
        position: 'relative',
        backgroundRepeat: 'repeat-y'
      }}>
        {/* メインガラスコンテナ */}
        <div style={{
          maxWidth: '400px',
          maxHeight: '100%'
        }}>
          {/* ロゴセクション */}
          <div style={{
            marginTop: '-50px',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <img 
              src={SwayLogo} 
              alt="Sway Logo" 
              style={{
                height: 'auto',
                width: '80px',
                maxWidth: '100%',
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))'
              }} 
            />
          </div>
          
          {/* ヘッダーメッセージ */}
          <div style={{
            padding: '10px 30px',
            textAlign: 'center',
          }}>
            <h1 style={{
              fontSize: '16px',
              fontWeight: '900',
              color: 'rgba(255, 255, 255, 0.95)',
              margin: '0 0 0 0',
              lineHeight: '2',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
            }}>
              {/* 🆕 地名を表示 */}
              {getDisplayLocationName()}にスポット作成中🎉<br/>
              投票→完成🙌<br/>
              🎧誰がどんな音楽を聴いてるか分かるよ！
              
              {/* 🆕 地名取得中のローディング表示 */}
              {locationLoading && (
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginTop: '8px'
                }}>
                  📍地名取得中...
                </div>
              )}
            </h1>
            <div style={{
              display: 'inline-block',
              background: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              padding: '8px 20px',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}>
              <p style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.9)',
                margin: 0,
                fontWeight: '600'
              }}>
                あと{remainingVotes}人
              </p>
            </div>
          </div>
  
          {/* マップエリア */}
          <div style={{
            position: 'relative',
            height: '125px',
            margin: '0 30px',
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${MapImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* エリア名のガラスオーバーレイ */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '15px',
                fontWeight: '700',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
              }}>
                {/* 🆕 地名と元のエリア名を両方表示 */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '15px'}}>
                    {areaInfo.area_name}
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          {/* エリア情報セクション */}
          <div style={{ padding: '18px 20px' }}>
              {/* 投票したユーザー */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '12px 16px 16px 12px',
                  boxShadow: '0 25px 45px rgba(0, 0, 0, 0.1)',
                }}>
                  {/* Swayロゴ */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '8px',
                  }}>
                    <img 
                      src={SwayLogo} 
                      alt="Sway Logo" 
                      style={{
                        height: '20px',
                        width: 'auto',
                        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                      }} 
                    />
                  </div>

                  {/* タイトル */}
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'rgba(255, 255, 255, 0.95)',
                    marginBottom: '8px',
                    textAlign: 'center',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    投票済みのユーザー
                  </h3>
                  
                  {/* ユーザーグリッド */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gridTemplateRows: 'repeat(2, 1fr)',
                    gap: '6px',
                  }}>
                    {Array.from({ length: 10 }).map((_, index) => {
                      const voter = areaInfo.voters_info[index];
                      if (voter) {
                        return (
                          <div
                            key={voter.vote_uid}
                            style={{
                              aspectRatio: '1',
                              borderRadius: '16px',
                              overflow: 'hidden',
                              background: 'rgba(255, 255, 255, 0.15)',
                              backdropFilter: 'blur(15px)',
                              WebkitBackdropFilter: 'blur(15px)',
                              
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                              position: 'relative'
                            }}
                          >
                            {voter.medium_icon_url ? (
                              <img
                                src={voter.medium_icon_url}
                                alt={voter.nickname}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  // 画像読み込み失敗時のフォールバック
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    const fallback = parent.querySelector('.fallback-text') as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <span 
                              className="fallback-text"
                              style={{
                                fontSize: '24px',
                                fontWeight: '700',
                                color: 'rgba(255, 255, 255, 0.9)',
                                display: voter.medium_icon_url ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              {voter.nickname.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        );
                      } else {
                        return (
                          <div
                            key={`empty-${index}`}
                            style={{
                              aspectRatio: '1',
                              borderRadius: '16px',
                              background: 'rgba(255, 255, 255, 0.03)',
                              backdropFilter: 'blur(5px)',
                              WebkitBackdropFilter: 'blur(5px)',
                              border: '1.5px solid rgba(255, 255, 255, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <span style={{ 
                              color: 'rgba(255, 255, 255, 0.3)', 
                              fontSize: '32px',
                              fontWeight: '200'
                            }}>
                              
                            </span>
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              </div>

      
            {/* 投票ボタン */}
            <button
              onClick={() => {
                const currentUrl = window.location.href;
                // 🆕 修正：URLからcodeを取得してディープリンクを生成
                const appUrl = generateDeepLinkUrl(areaId!);
                
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(currentUrl)
                    .then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 3000);
                      console.log('🚀 ディープリンク遷移:', appUrl);
                      window.location.href = appUrl;
                      setTimeout(() => goToAppStore(), 1500);
                    })
                    .catch(err => {
                      setManualCopyNeeded(true);
                      console.log('🚀 ディープリンク遷移 (コピー失敗):', appUrl);
                      window.location.href = appUrl;
                      setTimeout(() => goToAppStore(), 1500);
                    });
                } else {
                  setManualCopyNeeded(true);
                  console.log('🚀 ディープリンク遷移 (クリップボード非対応):', appUrl);
                  window.location.href = appUrl;
                  setTimeout(() => goToAppStore(), 1500);
                }
              }}
              disabled={!areaInfo.is_votable || isVoting}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: '700',
                color: areaInfo.is_votable ? '#333333' : 'rgba(255, 255, 255, 0.5)',
                background: areaInfo.is_votable 
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: areaInfo.is_votable 
                  ? '1px solid rgba(255, 255, 255, 0.5)'
                  : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                cursor: areaInfo.is_votable ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease',
                opacity: isVoting ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (areaInfo.is_votable && !isVoting) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 255, 255, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (areaInfo.is_votable && !isVoting) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(255, 255, 255, 0.3)';
                }
              }}
            >
              {isVoting ? '投票中...' : areaInfo.is_votable ? '投票する🗳️' : areaInfo.votable_message}
            </button>
          </div>
        </div>
  
        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* ホバーエフェクトのためのスムージング */
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        `}</style>
      </div>
    );
  };
  
  export default VoteAreaPage;